import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import AccountButton from '../../../components/ui/newDesigns/AccountButton';
import Title from '../../../components/ui/newDesigns/Title';
import TotalValueCard from '../../../components/ui/newDesigns/TotalValueCard';
import Loader from '../../../components/ui/loader';

import { usePrivateLayout } from '../../../layout/PrivateLayoutContext';

import {
  getPortfolioOverview,
  setSelectedPortfolio
} from '../../../redux/portfolioOverview/portfolioOverview.slice';

import { getBusinessDays } from '../../../redux/businessDay/businessDay.slice';

function MyAccounts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loadingPortfolio, setLoadingPortfolio] = React.useState(false);

  const { user } = useSelector((state) => state.auth);

  const { totalValue, clientPortfolios, loadingPortfolioOverview } = useSelector(
    (state) => state.portfolioOverview
  );

  const { businessDays } = useSelector((state) => state.businessDay);

  const { setBannerChildren } = usePrivateLayout();

  const clientName = user?.personalClient?.name ?? '';
  const clientIdNr = user?.personalClient?.idNr ?? '';

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  useEffect(() => {
    setBannerChildren(
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
        <Title
          title="Portfolio Overview"
          subtitle={clientName ? `${clientName} - ID: ${clientIdNr}` : `ID: ${clientIdNr}`}
        />

        <TotalValueCard total={loadingPortfolioOverview ? 'R 0.00' : formatCurrency(totalValue)} />
      </Box>
    );

    return () => {
      setBannerChildren(null);
    };
  }, [setBannerChildren, clientName, clientIdNr, loadingPortfolioOverview, totalValue]);

  // Fetch the latest business day.
  useEffect(() => {
    dispatch(
      getBusinessDays({
        countryCode: 'ZA',
        lag: -2
      })
    )
      .unwrap()
      .catch((error) => {
        console.error('MyAccounts: Business Days error:', error);
      });
  }, [dispatch]);

  // Fetch the portfolio overview using the business-day date.
  useEffect(() => {
    const clientId = user?.personalClient?.id;

    if (!clientId) {
      console.log('MyAccounts: personalClient.id not available yet');
      return;
    }

    if (!businessDays?.date) {
      return;
    }

    const latestBalanceDate = dayjs(businessDays.date);

    if (!latestBalanceDate.isValid()) {
      console.error('MyAccounts: Invalid business day:', businessDays.date);
      return;
    }

    const balanceDate = latestBalanceDate.format('YYYY-MM-DD');

    const request = {
      clientId,
      fromDate: balanceDate,
      toDate: balanceDate
    };

    setLoadingPortfolio(true);

    dispatch(getPortfolioOverview(request))
      .unwrap()
      .catch((error) => {
        console.error('MyAccounts: Portfolio Overview error:', error);
      })
      .finally(() => {
        setLoadingPortfolio(false);
      });
  }, [dispatch, user?.personalClient?.id, businessDays?.date]);

  const handlePortfolioClick = (portfolio) => {
    const accountIds = (portfolio?.accountData ?? []).map((account) => account?.id).filter(Boolean);

    if (!accountIds.length) {
      console.log('MyAccounts: no account IDs found for portfolio', portfolio?.portfolioName);
      return;
    }

    dispatch(
      setSelectedPortfolio({
        portfolioName: portfolio?.portfolioName ?? 'Portfolio',
        accountIds
      })
    );

    navigate('/portfolio');
  };

  return (
    <>
      <Loader loading={loadingPortfolio} message="Loading portfolio overview..." />

      <Box
        width="100%"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
        {(clientPortfolios ?? []).map((portfolio) => {
          const portfolioBalance = portfolio?.balance?.[0]?.sumSys ?? 0;
          const accountNumber = portfolio?.accountData?.[0]?.accountNr ?? '';

          return (
            <AccountButton
              key={portfolio.portfolioName}
              text={portfolio.portfolioName}
              accountNumber={accountNumber}
              amount={formatCurrency(portfolioBalance)}
              onClick={() => handlePortfolioClick(portfolio)}
            />
          );
        })}
      </Box>
    </>
  );
}

export default MyAccounts;
